/** Matches Page Settings → Button 1 / Button 2 label fields and Elements CTAs. */
export const MT_CTA_SLOT_ATTR = 'data-mt-cta-slot' as const
/** Matches Page Settings → Text link label field (Elements Text link block). */
export const MT_TEXT_LINK_SLOT_ATTR = 'data-mt-text-link-slot' as const

function parseEmailHtml(html: string): Document | null {
  try {
    const p = new DOMParser()
    return p.parseFromString(html, 'text/html')
  } catch {
    return null
  }
}

/**
 * Find the anchor that represents “primary” (Button 1) or “secondary” (Button 2) CTA text.
 * Prefers {@link MT_CTA_SLOT_ATTR}, then Elements classes, then mock template classes.
 */
export function findEmailCtaAnchor(
  doc: Document,
  slot: 1 | 2,
): HTMLAnchorElement | null {
  const bySlot = doc.querySelector(
    `a[${MT_CTA_SLOT_ATTR}="${slot}"]`,
  ) as HTMLAnchorElement | null
  if (bySlot) return bySlot

  if (slot === 1) {
    const primaryEl = doc.querySelector(
      'a.e-rte-email-btn:not(.e-rte-email-btn--alt):not(.e-rte-email-btn--outline)',
    ) as HTMLAnchorElement | null
    if (primaryEl) return primaryEl

    const mockPrimary = doc.querySelector(
      [
        'a.mt-email-tpl__btn--outreach-primary',
        'a.mt-email-tpl__btn--welcome-cta',
        'a.mt-email-tpl__btn--contact-teal',
        'a.mt-email-tpl__btn--year-end-cta',
      ].join(','),
    ) as HTMLAnchorElement | null
    if (mockPrimary) return mockPrimary

    const recall = doc.querySelector('a.mt-email-tpl__btn--recall-primary')
    if (recall) return recall as HTMLAnchorElement

    const anyMock = doc.querySelector('a.mt-email-tpl__btn')
    return anyMock as HTMLAnchorElement | null
  }

  const altEl = doc.querySelector(
    'a.e-rte-email-btn--alt',
  ) as HTMLAnchorElement | null
  if (altEl) return altEl

  const mockSec = doc.querySelector(
    'a.mt-email-tpl__btn--outreach-secondary',
  ) as HTMLAnchorElement | null
  if (mockSec) return mockSec

  const recallAll = doc.querySelectorAll('a.mt-email-tpl__btn--recall-primary')
  if (recallAll.length >= 2) return recallAll[1] as HTMLAnchorElement

  return null
}

export function getEmailCtaLabel(html: string, slot: 1 | 2): string {
  const doc = parseEmailHtml(html)
  if (!doc) return ''
  const a = findEmailCtaAnchor(doc, slot)
  return a?.textContent?.trim() ?? ''
}

export function setEmailCtaLabelInHtml(
  html: string,
  slot: 1 | 2,
  label: string,
): string {
  const doc = parseEmailHtml(html)
  if (!doc) return html
  const a = findEmailCtaAnchor(doc, slot)
  if (!a) return html
  a.textContent = label
  return doc.body.innerHTML
}

export function hasSecondCtaInHtml(html: string): boolean {
  const doc = parseEmailHtml(html)
  if (!doc) return false
  return findEmailCtaAnchor(doc, 2) !== null
}

export function findEmailTextLinkAnchor(
  doc: Document,
): HTMLAnchorElement | null {
  const bySlot = doc.querySelector(
    `a[${MT_TEXT_LINK_SLOT_ATTR}="1"]`,
  ) as HTMLAnchorElement | null
  if (bySlot) return bySlot

  const byClass = doc.querySelector(
    'a.e-rte-email-text-link-cta',
  ) as HTMLAnchorElement | null
  if (byClass) return byClass

  const legacy = doc.querySelector(
    'a[href][style*="text-decoration:underline"][style*="--mt-email-cta-btn-bg"]',
  ) as HTMLAnchorElement | null
  if (legacy) return legacy

  return null
}

export function getEmailTextLinkLabel(html: string): string {
  const doc = parseEmailHtml(html)
  if (!doc) return ''
  const a = findEmailTextLinkAnchor(doc)
  return a?.textContent?.trim() ?? ''
}

export function setEmailTextLinkLabelInHtml(
  html: string,
  label: string,
): string {
  const doc = parseEmailHtml(html)
  if (!doc) return html
  const a = findEmailTextLinkAnchor(doc)
  if (!a) return html
  if (!a.classList.contains('e-rte-email-text-link-cta')) {
    a.classList.add('e-rte-email-text-link-cta')
  }
  a.setAttribute(MT_TEXT_LINK_SLOT_ATTR, '1')
  a.textContent = label
  return doc.body.innerHTML
}
